import { useQueryClient } from "@tanstack/vue-query";
import { identifyPosthogUser } from "~/composables/useAuth";

// Cached lists that belong to the signed-in account (public lists like Community are left alone)
const ACCOUNT_QUERY_ROOTS = new Set(["collections", "selection-edit-works"]);
const ACCOUNT_PUBLISHED_KEYS = new Set(["manage", "mine-status", "edit", "mine"]);

export default defineNuxtPlugin(async (nuxtApp) => {
  const supabase = useSupabaseClient();

  // Auto-sync QR attribution between client cookie and localStorage
  try {
    const clientQrCookie = useCookie<any>("qam_qr_client", {
      maxAge: 400 * 24 * 60 * 60,
      path: "/",
      sameSite: "lax",
    });
    if (clientQrCookie.value) {
      // Cookie exists -> backup to localStorage permanently
      localStorage.setItem(
        "qam_qr_source",
        typeof clientQrCookie.value === "string"
          ? clientQrCookie.value
          : JSON.stringify(clientQrCookie.value)
      );
    } else {
      // Cookie was cleared or missing -> restore from localStorage
      const backupQr = localStorage.getItem("qam_qr_source");
      if (backupQr) {
        try {
          clientQrCookie.value = JSON.parse(backupQr);
        } catch {
          clientQrCookie.value = backupQr;
        }
      }
    }
  } catch (err) {
    console.error("[auth-init] QR attribution sync error:", err);
  }

  let activeFetchPromise: Promise<any> | null = null;
  let activeFetchUserId: string | null = null;
  let lastFetchedUserId: string | null = null;
  let lastFetchedToken: string | null = null;

  async function fetchCollections(session: any) {
    const userId = session?.user?.id;
    if (!userId) return;

    const token = session?.access_token;
    if (lastFetchedToken === token && lastFetchedUserId === userId) {
      return;
    }

    if (activeFetchPromise && activeFetchUserId === userId) {
      return activeFetchPromise;
    }

    activeFetchUserId = userId;
    activeFetchPromise = (async () => {
      try {
        const appState = useAppState();
        const headers: Record<string, string> = {};
        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }

        const { collections } = await $fetch<{ collections: any[] }>("/api/collection", {
          headers,
        });
        appState.collections.value = collections;

        lastFetchedUserId = userId;
        lastFetchedToken = token;
      } catch (err) {
        console.error("[auth-init] Failed to fetch collections:", err);
        lastFetchedUserId = null;
        lastFetchedToken = null;
      } finally {
        activeFetchPromise = null;
        activeFetchUserId = null;
      }
    })();

    return activeFetchPromise;
  }

  async function handleAnonymousSession(session: any) {
    // Identify anonymous user in PostHog with Supabase UID
    if (session?.user) {
      identifyPosthogUser(session.user);
    }

    const backupQr = localStorage.getItem("qam_qr_source");
    const isMigrationPending = localStorage.getItem("sb-anon-migration-pending") === "true";
    const isQrSynced = localStorage.getItem("qam_qr_synced") === "true";

    // Call server if migration is pending OR if QR backup needs re-hydrating/syncing
    if (!isMigrationPending || (backupQr && !isQrSynced)) {
      try {
        const headers: Record<string, string> = {};
        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }
        const res = await $fetch<{ ok: boolean; qrSource?: any }>("/api/auth/save-anon-session", {
          method: "POST",
          headers,
          body: {
            backupQrSource: backupQr,
          },
        });
        if (res?.qrSource) {
          localStorage.setItem("qam_qr_source", JSON.stringify(res.qrSource));
          localStorage.setItem("qam_qr_synced", "true");
        }
        localStorage.setItem("sb-anon-migration-pending", "true");
      } catch (err) {
        console.error("[auth-init] Failed to save anonymous session:", err);
      }
    }
    // Fetch collections for this anonymous user immediately
    await fetchCollections(session);
  }

  async function handlePermanentSession(session: any) {
    // Identify user in PostHog
    if (session?.user) {
      identifyPosthogUser(session.user);
    }

    // Only call migration if there is a pending anonymous session to migrate
    if (localStorage.getItem("sb-anon-migration-pending") === "true") {
      try {
        const headers: Record<string, string> = {};
        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }
        await $fetch("/api/auth/migrate-anonymous-data", {
          method: "POST",
          headers,
        });
      } catch (err) {
        console.error("[auth-init] Failed to migrate anonymous data:", err);
      } finally {
        localStorage.removeItem("sb-anon-migration-pending");
      }
    }
    // Fetch collections for this permanent user immediately
    await fetchCollections(session);
  }

  try {
    // Register listener to handle initial load and dynamic logins/signups/signouts
    supabase.auth.onAuthStateChange((event, session) => {
      // Run asynchronously in the background to avoid blocking the Supabase auth flow
      (async () => {
        try {
          if (event === "SIGNED_OUT") {
            localStorage.removeItem("sb-anon-migration-pending");
            resetNudgeState();
            const appState = useAppState();
            appState.collections.value = [];
            lastFetchedUserId = null;
            lastFetchedToken = null;

            // Forget the previous account's cached lists so the next account never briefly sees them
            try {
              const queryClient = nuxtApp.vueApp.runWithContext(() => useQueryClient());
              queryClient.removeQueries({
                predicate: (query) => {
                  const [root, scope] = query.queryKey as [string, string?];
                  return (
                    ACCOUNT_QUERY_ROOTS.has(root) ||
                    (root === "published-selections" && !!scope && ACCOUNT_PUBLISHED_KEYS.has(scope))
                  );
                },
              });
            } catch (err) {
              console.error("[auth-init] Failed to clear account queries on sign out:", err);
            }
          }

          if (session?.user) {
            const isAnon = session.user.is_anonymous || session.user.app_metadata?.provider === "anonymous";
            if (isAnon) {
              await handleAnonymousSession(session);
            } else {
              await handlePermanentSession(session);
            }
          } else if (event === "INITIAL_SESSION" || event === "SIGNED_OUT") {
            // No session exists, sign in anonymously
            const { error } = await supabase.auth.signInAnonymously();
            if (error) {
              console.error("[auth-init] Failed to sign in anonymously:", error.message);
            }
          }
        } catch (err) {
          console.error("[auth-init] Error in onAuthStateChange listener:", err);
        }
      })();
    });
  } catch (err) {
    console.error("[auth-init] Session initialization error:", err);
  }
});

